let timezoneOverride: string | undefined
const timezoneFormatters = new Map<string, Intl.DateTimeFormat>()
const timezoneNameFormatters = new Map<string, Intl.DateTimeFormat>()

interface TimezoneDateParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format()
    return true
  } catch {
    return false
  }
}

export function setViewerTimezone(timezone: string | undefined): void {
  timezoneOverride = timezone && isValidTimezone(timezone) ? timezone : undefined
}

export function viewerTimezoneOverride(): string | undefined {
  return timezoneOverride
}

export function viewerTimezone(): string {
  return timezoneOverride ?? browserTimezone()
}

export function supportedTimezones(): string[] {
  const timezones = new Set(Intl.supportedValuesOf("timeZone"))
  timezones.add("UTC")
  timezones.add(browserTimezone())
  return [...timezones].sort()
}

export function calendarDateInTimezone(timezone: string, timestamp: number): string {
  const { year, month, day } = timezoneDatePartsAt(timezone, timestamp)
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

export function viewerCalendarDate(timestamp: number = Date.now()): string {
  return calendarDateInTimezone(viewerTimezone(), timestamp)
}

export function millisecondsUntilViewerDayChanges(timestamp: number = Date.now()): number {
  return millisecondsUntilCalendarDayChanges(viewerTimezone(), timestamp)
}

export function millisecondsUntilCalendarDayChanges(
  timezone: string,
  timestamp: number = Date.now()
): number {
  const currentDay = calendarDateInTimezone(timezone, timestamp)
  let lowerBound = timestamp
  let upperBound = timestamp + 48 * 60 * 60 * 1000

  while (upperBound - lowerBound > 1) {
    const midpoint = Math.floor((lowerBound + upperBound) / 2)
    if (calendarDateInTimezone(timezone, midpoint) === currentDay) lowerBound = midpoint
    else upperBound = midpoint
  }

  return upperBound - timestamp
}

export function viewerTimezoneName(timestamp: number): string {
  const timezone = viewerTimezone()
  let formatter = timezoneNameFormatters.get(timezone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    })
    timezoneNameFormatters.set(timezone, formatter)
  }

  return (
    formatter.formatToParts(timestamp).find((part) => part.type === "timeZoneName")?.value ?? ""
  )
}

export function timezoneDatePartsAt(timezone: string, timestamp: number): TimezoneDateParts {
  let formatter = timezoneFormatters.get(timezone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
    timezoneFormatters.set(timezone, formatter)
  }

  let year = 0
  let month = 0
  let day = 0
  let hour = 0
  let minute = 0
  let second = 0
  for (const part of formatter.formatToParts(timestamp)) {
    const value = Number(part.value)
    if (part.type === "year") year = value
    if (part.type === "month") month = value
    if (part.type === "day") day = value
    if (part.type === "hour") hour = value === 24 ? 0 : value
    if (part.type === "minute") minute = value
    if (part.type === "second") second = value
  }

  return { year, month, day, hour, minute, second }
}
